import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  Order,
  OrderProduct,
  OrderStatus,
} from '../../../../../models/order.model';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { uniqueValueValidator } from '../../../../product/validators/variation-attributeId.validator';
import { ProductIdentifierTypeService } from '../../../../../services/product-identifier-type.service';
import { DropdownOption } from '../../../../../../shared/models/dropdowm.model';
import { ApiResult } from '../../../../../../shared/models/api-result.model';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { FormErrorComponent } from '../../../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../../../shared/components/form-server-error/form-server-error.component';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { OrderService } from '../../../../../services/order.service';
import { CreateProduct } from '../../../../../models/product.model';
import {
  CreateProductIdentifiersRequest,
  UpdateProductIdentifiersRequest,
} from '../../models/order-details.model';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroupService } from '../../../../../../shared/services/form-group.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PhoneFormatPipe } from '../../../../../../shared/pipes/format-phone.pipe';

@Component({
  selector: 'app-order-details-product',
  imports: [
    ReactiveFormsModule,
    SelectModule,
    FormErrorComponent,
    FormServerErrorComponent,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
  ],
  providers: [PhoneFormatPipe],
  templateUrl: './order-details-product.component.html',
})
export class OrderDetailsProductComponent implements OnInit {
  order = input<Order | null>(null);
  product = input<OrderProduct | null>(null);
  @Output() loadData = new EventEmitter<string>();

  private fb = inject(FormBuilder);
  private productIdentifierTypeService = inject(ProductIdentifierTypeService);
  private messageService = inject(MessageService);
  private orderService = inject(OrderService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    productIdentifiers: this.fb.array([], {
      validators: [uniqueValueValidator('productIdentifierTypeId')],
    }),
  });

  loading = signal(false);
  productIdentifiersSaved = signal(false);
  productIdentifierLoading = signal<boolean[]>([]);
  productIdentifierDropdown = signal<DropdownOption[][]>([]);

  orderSent = computed(() => {
    if (!this.order) return false;
    return [
      OrderStatus.Shipped,
      OrderStatus.Delivered,
      OrderStatus.Cancelled,
    ].includes(this.order()!.status);
  });

  productIdentifiersLocked = computed(() => {
    if (!this.order()) return true;

    return (
      this.productIdentifiersSaved() ||
      this.orderSent() ||
      this.order()!.status !== OrderStatus.Reserved
    );
  });

  get productIdentifiersArray(): FormArray {
    return this.formGroup.get('productIdentifiers') as FormArray;
  }

  ngOnInit(): void {
    const identifiers = this.order()?.productIdentifiers ?? [];

    if (identifiers.length > 0) {
      identifiers.forEach((pi, index) => {
        this.addProductIdentifier(true);

        const typeOption: DropdownOption = {
          label: pi.productIdentifierTypeName,
          value: pi.productIdentifierTypeId,
        };

        const group = this.productIdentifiersArray.at(index) as FormGroup;
        group.patchValue({
          id: pi.id,
          productIdentifierTypeId: typeOption,
          value: pi.value,
          note: pi.note,
        });

        this.loadProductIdentifierDropdown(index, true, typeOption);
      });
    } else {
      this.addProductIdentifier(false);
    }
  }

  addProductIdentifier(fromLoad: boolean = false): void {
    const group = this.fb.group({
      id: [null],
      productIdentifierTypeId: [
        { value: null, disabled: this.productIdentifiersLocked() },
        [Validators.required],
      ],
      value: [
        { value: null, disabled: this.productIdentifiersLocked() },
        [Validators.required],
      ],
      note: [null],
    });

    this.productIdentifiersArray.push(group);

    const dropdown = this.productIdentifierDropdown();
    dropdown.push([]);
    this.productIdentifierDropdown.set([...dropdown]);

    const loading = this.productIdentifierLoading();
    loading.push(false);
    this.productIdentifierLoading.set([...loading]);

    if (!fromLoad) {
      const index = this.productIdentifiersArray.length - 1;
      this.loadProductIdentifierDropdown(index, false);
    }
  }

  removeProductIdentifier(index: number): void {
    this.productIdentifiersArray.removeAt(index);

    const dropdown = this.productIdentifierDropdown();
    dropdown.splice(index, 1);
    this.productIdentifierDropdown.set([...dropdown]);

    const loading = this.productIdentifierLoading();
    loading.splice(index, 1);
    this.productIdentifierLoading.set([...loading]);
  }

  onProductIdentifierChange(index: number, value: any): void {
    const group = this.productIdentifiersArray.at(index) as FormGroup;

    group.patchValue({
      productIdentifierTypeId: value,
      value: null,
    });
  }

  getProductIdentifierControl(index: number, controlName: string) {
    return (this.productIdentifiersArray.at(index) as FormGroup).get(
      controlName,
    );
  }

  saveProductIdentifiers(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const orderId = this.order()!.id;
    const data = this.productIdentifiersArray.value.map((x: any) => {
      return {
        id: x.id,
        productIdentifierTypeId: x.productIdentifierTypeId.value,
        value: x.value,
        note: x.note,
      };
    });

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (this.order()?.productIdentifiers.length === 0) {
      request$ = this.orderService.createProductIdentifiers(
        orderId,
        data as CreateProductIdentifiersRequest[],
      );
    } else {
      request$ = this.orderService.updateProductIdentifiers(
        orderId,
        data as UpdateProductIdentifiersRequest[],
      );
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);
        this.productIdentifiersSaved.set(true);
        this.lockProductIdentifierControls();

        if (this.order()) this.loadData.emit(this.order()!.id);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Identificadores de produto salvos com sucesso',
          life: 2000,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            const apiError = e.error ?? {};
            const serverErrors = apiError.errors;
            const hasServerErrors =
              serverErrors &&
              typeof serverErrors === 'object' &&
              Object.keys(serverErrors).length > 0;

            if (hasServerErrors) {
              this.formGroupService.addServerErrors(
                serverErrors,
                this.formGroup,
              );
            }

            const fieldErrorMessage = hasServerErrors
              ? Object.values(serverErrors)
                  .map((error) =>
                    Array.isArray(error) ? error.join(', ') : String(error),
                  )
                  .join(' ')
              : '';
            const detail = [apiError.message, fieldErrorMessage]
              .filter(Boolean)
              .join(': ');

            this.messageService.add({
              severity: 'warn',
              summary: 'Atenção',
              detail:
                detail ||
                'Não foi possível salvar os identificadores do produto',
              life: 3000,
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail:
                e.error?.message ||
                'Ocorreu um erro ao salvar os identificadores do produto',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private lockProductIdentifierControls(): void {
    this.productIdentifiersArray.controls.forEach((control) => {
      control.get('productIdentifierTypeId')?.disable();
      control.get('value')?.disable();
    });
  }

  private loadProductIdentifierDropdown(
    index: number,
    fromLoad: boolean,
    productIdentifierValue: DropdownOption | null = null,
  ): void {
    const loading = this.productIdentifierLoading();
    loading[index] = true;
    this.productIdentifierLoading.set([...loading]);

    this.productIdentifierTypeService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];

        const current = this.productIdentifierDropdown();
        current[index] = options;
        this.productIdentifierDropdown.set([...current]);

        const l = this.productIdentifierLoading();
        l[index] = false;
        this.productIdentifierLoading.set([...l]);

        if (fromLoad && productIdentifierValue?.value) {
          const group = this.productIdentifiersArray.at(index) as FormGroup;
          group
            .get('productIdentifierTypeId')
            ?.setValue(productIdentifierValue);
        }
      },
      error: () => {
        const l = this.productIdentifierLoading();
        l[index] = false;
        this.productIdentifierLoading.set([...l]);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar os identificadores do produto',
          life: 3000,
        });
      },
    });
  }
}
